/**
 * useCustomerActivitiesRealtime - Realtime para a timeline de atividades do Cliente
 *
 * Subscreve eventos Postgres Changes na tabela `crm_deal_activities` filtrados
 * por `customer_id` e invalida as queries que alimentam a
 * `CustomerActivityTimelinePremium` (aba Atividades e Resumo).
 *
 * Espelha o padrão do `useDealActivitiesRealtime` — filtro por customer_id,
 * canal único por montagem para evitar colisão ao reabrir o modal, e
 * cleanup obrigatório no unmount.
 *
 * @version 1.0.0
 * RELEASE: 2026-07-11 v1.0.0 - Criação inicial (paridade com useDealActivitiesRealtime)
 */
import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';

const logger = createLogger('CustomerActivitiesRealtime');

/**
 * Subscreve Realtime em `crm_deal_activities` para o cliente informado e
 * invalida os caches da timeline ao receber qualquer evento.
 *
 * IMPORTANTE: só cobre atividades vinculadas diretamente ao cliente
 * (customer_id). Atividades vinculadas via deal são cobertas quando o
 * respectivo modal de negócio estiver aberto — comportamento aceitável
 * pois a lista do cliente já é rebuscada sempre que a aba é reaberta.
 *
 * @param customerId - ID do cliente aberto (null/undefined = não subscreve)
 */
export function useCustomerActivitiesRealtime(customerId: string | null | undefined) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!customerId) return;

    logger.debug('Iniciando Realtime para atividades do cliente', { customerId });

    // Canal único por montagem para evitar erro de re-subscribe
    const channelName = `customer-activities-rt-${customerId}-${crypto.randomUUID()}`;

    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'crm_deal_activities',
          filter: `customer_id=eq.${customerId}`,
        },
        (payload) => {
          logger.debug('Realtime: crm_deal_activities alterado (cliente)', {
            eventType: payload.eventType,
            customerId,
          });
          queryClient.invalidateQueries({ queryKey: ['customer-activities-paginated', customerId] });
          queryClient.invalidateQueries({ queryKey: ['customer-activities', customerId] });
        }
      )
      .subscribe((status) => {
        logger.debug('Realtime customer activities subscription status:', status);
      });

    return () => {
      logger.debug('Removendo Realtime de atividades do cliente', { customerId });
      supabase.removeChannel(channel);
    };
  }, [customerId, queryClient]);
}
